// AVOIR model (Phase 4, 47030 locked): the pure rules behind the avoir UI. No React, no network. Money is exact text (cents as BigInt), never a float.
// The server (db/812) stays authoritative: these only shape the form, the cap hint and the wording.
import { cents, text } from '../../../pages/Sales/invoicing/invoicingModel.mjs';

export const parseFlag = (v) => v === true || String(v).toLowerCase() === 'true';
export const AVOIR_TONE = { draft: 'mut', issued: 'good', cancelled: 'crit' };
export const DOC_KINDS = ['invoice', 'proforma', 'avoir', 'voucher', 'slip'];

// the room left on the newest issued invoice that still has any: invoice total - (drafts + issued avoirs on it). null = no issued invoice covers the order.
export function avoirRoom(invoices, avoirs) {
  const live = (avoirs || []).filter(a => a.status !== 'cancelled');
  let best = null;
  for (const i of (invoices || [])) {
    if (i.kind !== 'invoice' || i.status !== 'issued') continue;
    const used = live.filter(a => a.invoice_id === i.id).reduce((s, a) => s + cents(a.amount), 0n);
    const room = cents(i.total_amount) - used;
    if (!best || room > cents(best.remaining)) best = { invoice_id: i.id, invoice_no: i.invoice_no, remaining: text(room > 0n ? room : 0n) };
  }
  return best;
}

// GET /sales-orders/:op/avoir-room -> the form's room: {kind:'invoice', remaining (the order's ONE pool), invoice_id/invoice_no (the invoice with the most room)} | {kind:'recognised', remaining} | null
export function roomOf(r) {
  if (r?.kind === 'invoice') {
    const best = (r.invoices || []).reduce((b, i) => (!b || cents(i.room) > cents(b.room) ? i : b), null);
    return { kind: 'invoice', remaining: text(cents(r.room)), invoice_id: best?.id || null, invoice_no: best?.invoice_no || '' };
  }
  return r?.kind === 'recognised' ? { kind: 'recognised', remaining: text(cents(r.room)), invoice_id: null, invoice_no: '' } : null;
}

// '' | i18n key under `errors.` : local pre-check of the form (the server re-checks everything)
export function validateAvoir({ operationId, amount, reason, date }, today, room) {
  if (!operationId) return 'order';
  if (!amount || cents(amount) <= 0n) return 'amount';
  if (room && cents(amount) > cents(room.remaining)) return 'cap';
  if (!String(reason || '').trim()) return 'reason';
  if (date && date > today) return 'future';
  return '';
}

// what a changed money response carries (all optional, only present when the model is ON)
export function offerOf(data) {
  const d = data || {};
  return { draft: d.avoir_draft || null, warning: d.avoir_warning || null, voucher: d.refund_voucher || null, slip: d.return_slip || null, payment: d.payment_voucher || null };
}
export const voucherNo = (v) => (v && (v.voucher_no || v.document_no || v.no)) || '';
export const slipNo = (s) => (s && (s.slip_no || s.document_no || s.no)) || '';

// the 'Documents' register: one row per document, grouped for the kind filter chips. `document_kind` is invoice | proforma | avoir_* | refund_voucher | return_slip | payment_voucher
export function kindGroup(k) {
  const s = String(k || '');
  if (s === 'invoice' || s === 'proforma') return s;
  if (s.startsWith('avoir')) return 'avoir';
  if (s === 'refund_voucher' || s === 'payment_voucher') return 'voucher';
  if (s === 'return_slip') return 'slip';
  return 'other';
}

// refusal -> the user's language (the API sends error + error_fr by CODE)
export const refusalText = (res, lang) => { const b = res?.data || {}; return (lang && lang.startsWith('fr') && b.error_fr) || b.error || `HTTP ${res?.status || ''}`.trim(); };

// statement closing line: customerNet wording by the sign of the balance (text, exact). owes | credit | zero
export function closingSide(balance) { const c = cents(balance); return c > 0n ? 'owes' : c < 0n ? 'credit' : 'zero'; }
export const absText = (v) => { const c = cents(v); return text(c < 0n ? -c : c); };

// ── credit note FROM EVENTS (owner 2026-10-09): GET /sales-orders/:op/avoir-sources -> the rows the form ticks. Exact text money (cents BigInt), never a float.
const lowest = (a, b) => (cents(a) < cents(b) ? a : b);
// one source -> a form row. cap = the most the line may carry (an open event: what is left; a draft line: the event itself); def = the amount ticked by default (open: min(remaining, invoice room); draft: its own line amount)
export function sourceRowOf(s, room) {
  const done = s.status === 'credited', draft = s.status === 'draft';
  const cap = draft ? s.amount : s.remaining;
  const def = draft ? s.credited : (room != null ? lowest(s.remaining, room) : s.remaining);
  return { key: `${s.source_kind}:${s.source_ref}`, kind: s.source_kind, ref: s.source_ref, status: s.status, done, draft, cap, def, label: s.label, label_fr: s.label_fr, draft_id: s.draft_id || null, date: s.date, amount: s.amount, credited: s.credited, remaining: s.remaining, avoir_no: s.avoir_no || '', slip_no: s.slip_no || '', tickable: !done && cents(def) > 0n };
}
export const sourceRows = (data) => ((data && data.sources) || []).map(s => sourceRowOf(s, data.reconcile && data.reconcile.room));
// '' | 'amount' | 'cap': a ticked line's typed (already normalised) amount against its cap
export const lineError = (amount, cap) => (!amount || cents(amount) <= 0n ? 'amount' : cents(amount) > cents(cap) ? 'cap' : '');
// ticked rows (+ the admin's Other correction) -> the POST body lines
export function buildLines(rows, sel, other) {
  const out = rows.filter(r => r.tickable && sel[r.key] && sel[r.key].on).map(r => ({ source_kind: r.kind, source_ref: r.ref, amount: sel[r.key].amount }));
  if (other && other.on) out.push({ source_kind: 'other', amount: other.amount, reason: String(other.reason || '').trim() });
  return out;
}
// why the sources list is empty / spent (i18n key under `form.nothing.`): allCredited | noInvoice (not invoiced, nothing delivered) | noEvents (invoiced, no return / discount recorded)
export function nothingWhy(data) {
  const rows = sourceRows(data);
  if (rows.length) return 'allCredited';
  const r = (data && data.reconcile) || {};
  return r.kind !== 'invoice' && cents(r.recognised || 0) === 0n ? 'noInvoice' : 'noEvents';
}
export const hasCreditable = (rows) => rows.some(r => r.tickable);
